import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Releases from "@/pages/Releases";
import ReleaseDetail from "@/pages/ReleaseDetail";
import SubmitRelease from "@/pages/SubmitRelease";
import Royalties from "@/pages/Royalties";
import Tickets from "@/pages/Tickets";
import Users from "@/pages/Users";
import Artists from "@/pages/Artists";
import Labels from "@/pages/Labels";
import Files from "@/pages/Files";
import Settings from "@/pages/Settings";
import Register from "@/pages/Register";
import Contracts from "@/pages/Contracts";
import Takedowns from "@/pages/Takedowns";
import Audit from "@/pages/Audit";
import CompleteKYC from "@/pages/CompleteKYC";
import ApprovalQueue from "@/pages/ApprovalQueue";
import Withdrawals from "@/pages/Withdrawals";
import BulkUpload from "@/pages/BulkUpload";
import CustomFields from "@/pages/CustomFields";
import UserDetail from "@/pages/UserDetail";
import ManagedView from "@/pages/ManagedView";
import RosterDetail from "@/pages/RosterDetail";
import ManagerMapping from "@/pages/ManagerMapping";
import { Loader2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

function KYCPending() {
  const { user, logout } = useAuth();
  return (
    <div className="dark flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center" data-testid="kyc-pending-screen">
      <div className="rounded-full bg-amber-500/10 p-4"><Clock className="h-10 w-10 text-amber-400" /></div>
      <h1 className="font-heading text-2xl font-bold">KYC Under Review</h1>
      <p className="max-w-md text-muted-foreground">Thanks {user?.name}. Your KYC has been submitted and is awaiting Super Admin verification. You'll get full access once it's approved.</p>
      <Button variant="secondary" onClick={logout} data-testid="kyc-pending-logout">Log out</Button>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") {
    if (!user.kyc_completed) return <Navigate to="/complete-kyc" replace />;
    if (!user.is_approved) return <KYCPending />;
  }
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />
      <Route path="/complete-kyc" element={<CompleteKYC />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/releases" element={<Protected><Releases /></Protected>} />
      <Route path="/releases/new" element={<Protected><SubmitRelease /></Protected>} />
      <Route path="/releases/:id" element={<Protected><ReleaseDetail /></Protected>} />
      <Route path="/royalties" element={<Protected><Royalties /></Protected>} />
      <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/users/:id" element={<Protected><UserDetail /></Protected>} />
      <Route path="/approval-queue" element={<Protected><ApprovalQueue /></Protected>} />
      <Route path="/managed" element={<Protected><ManagedView /></Protected>} />
      <Route path="/withdrawals" element={<Protected><Withdrawals /></Protected>} />
      <Route path="/bulk-upload" element={<Protected><BulkUpload /></Protected>} />
      <Route path="/custom-fields" element={<Protected><CustomFields /></Protected>} />
      <Route path="/artists" element={<Protected><Artists /></Protected>} />
      <Route path="/labels" element={<Protected><Labels /></Protected>} />
      <Route path="/files" element={<Protected><Files /></Protected>} />
      <Route path="/contracts" element={<Protected><Contracts /></Protected>} />
      <Route path="/takedowns" element={<Protected><Takedowns /></Protected>} />
      <Route path="/audit" element={<Protected><Audit /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/manager-mapping" element={<Protected><ManagerMapping /></Protected>} />
      <Route path="/roster/label/:id" element={<Protected><RosterDetail type="label" /></Protected>} />
      <Route path="/roster/artist/:id" element={<Protected><RosterDetail type="artist" /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
